import { useEffect, useState } from 'react';
import { Timeline, Badge, Group, Text, Loader, Box } from '@mantine/core';
import useApi from '../hooks/useApi';
import { fetchLeadTasks, fetchLeadEvents } from '../services/api';

const STATUS_COLORS = {
  queued: 'gray',
  in_flight: 'yellow',
  sent: 'blue',
  clicked: 'teal',
  bounced: 'red',
  failed: 'red',
  cancelled: 'gray',
  skipped: 'gray',
};

const EVENT_COLORS = {
  sent: 'blue',
  click: 'teal',
  advance: 'grape',
  note: 'gray',
  booked: 'green',
  dwell: 'orange',
};

function formatActor(sub) {
  if (!sub || sub === 'system' || sub === 'seed') return 'System';
  if (sub.endsWith('@clients')) return 'Bot';
  if (sub.startsWith('auth0|')) return 'User';
  return 'System';
}

function buildTimelineEntries(tasks, events) {
  const entries = [];

  for (const task of tasks) {
    entries.push({
      ts: task.createdAt,
      key: `task-${task.id}-created`,
      kind: 'task',
      color: 'gray',
      title: `Task queued - Touch ${task.touchId?.slice(0, 6) ?? '?'} / ${task.channel}`,
      detail: `dueAt ${task.dueAt}`,
    });

    if (task.lastTransitionAt && task.lastTransitionAt !== task.createdAt) {
      entries.push({
        ts: task.lastTransitionAt,
        key: `task-${task.id}-state-${task.status}`,
        kind: 'task',
        color: STATUS_COLORS[task.status] || 'gray',
        title: `Task ${task.status}`,
        detail: `by ${formatActor(task.lastTransitionBy)}${task.attemptCount ? ` - attempt ${task.attemptCount}` : ''}`,
        messageBody: task.status === 'sent' ? (task.messageBody || null) : null,
        channel: task.channel || null,
      });
    }
  }

  for (const ev of events) {
    const summary = ev.type === 'advance' && ev.payload
      ? `${ev.payload.from} -> ${ev.payload.to}${ev.payload.reason ? ` (${ev.payload.reason})` : ''}`
      : ev.type === 'click' && ev.payload
        ? `linkId ${ev.payload.linkId?.slice(0, 8) ?? '?'}${ev.payload.qualifying ? ' - qualifying' : ''}`
        : ev.type === 'dwell' && ev.payload
          ? `${ev.payload.elapsedSeconds ?? '?'}s on page, scrolled ${ev.payload.maxScrollPct ?? 0}%`
        : ev.type === 'sent' && ev.payload
          ? `task ${ev.payload.taskId?.slice(0, 8) ?? '?'} via ${ev.payload.channel || '?'}`
          : '';

    const actorLabel = formatActor(ev.actor);
    entries.push({
      ts: ev.ts,
      key: `event-${ev.id}`,
      kind: 'event',
      color: EVENT_COLORS[ev.type] || 'gray',
      title: ev.type === 'dwell' ? `Dwell — ${ev.payload?.elapsedSeconds ?? '?'}s, ${ev.payload?.maxScrollPct ?? 0}% scrolled` : `${ev.type.toUpperCase()} event`,
      detail: [summary, `by ${actorLabel}`].filter(Boolean).join(' · '),
    });
  }

  entries.sort((a, b) => (a.ts < b.ts ? 1 : -1));
  return entries;
}

export default function LeadTimeline({ leadId }) {
  const api = useApi();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [tasksRes, eventsRes] = await Promise.all([
          fetchLeadTasks(api, leadId),
          fetchLeadEvents(api, leadId),
        ]);
        if (cancelled) return;
        setEntries(buildTimelineEntries(tasksRes.tasks || [], eventsRes.events || []));
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load timeline');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [api, leadId]);

  if (loading) {
    return (
      <Group justify="center" mt="md">
        <Loader size="sm" />
      </Group>
    );
  }

  if (error) {
    return <Text c="red">{error}</Text>;
  }

  if (entries.length === 0) {
    return <Text c="dimmed" mt="md">No activity yet.</Text>;
  }

  return (
    <Timeline active={entries.length - 1} bulletSize={18} lineWidth={2} mt="md">
      {entries.map((entry) => (
        <Timeline.Item key={entry.key} color={entry.color}>
          <Group gap="xs" wrap="nowrap" align="center">
            <Badge variant="light" color={entry.color} size="sm">
              {entry.kind === 'task' ? 'TASK' : 'EVENT'}
            </Badge>
            <Text fw={500} size="sm">{entry.title}</Text>
          </Group>
          {entry.detail && <Text c="dimmed" size="xs" mt={2}>{entry.detail}</Text>}
          <Text c="dimmed" size="xs">{new Date(entry.ts).toLocaleString()}</Text>
          {entry.messageBody && (
            <Box
              mt={8}
              p="sm"
              style={{
                background: 'var(--mantine-color-blue-0)',
                borderRadius: '0 12px 12px 12px',
                border: '1px solid var(--mantine-color-blue-2)',
                maxWidth: 420,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {entry.channel && (
                <Text size="xs" c="blue.6" fw={600} mb={4} tt="uppercase">
                  {entry.channel}
                </Text>
              )}
              <Text size="sm">{entry.messageBody}</Text>
            </Box>
          )}
        </Timeline.Item>
      ))}
    </Timeline>
  );
}
